/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListUserSessionClientsRequest,
  ListUserSessionClientsSecurity,
} from "../models/operations/listusersessionclients.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildUserSessionClientsInfiniteQuery,
  buildUserSessionClientsQuery,
  prefetchUserSessionClients,
  prefetchUserSessionClientsInfinite,
  queryKeyUserSessionClients,
  queryKeyUserSessionClientsInfinite,
  UserSessionClientsInfiniteQueryData,
  UserSessionClientsPageParams,
  UserSessionClientsQueryData,
} from "./userSessionClients.core.js";
export {
  buildUserSessionClientsInfiniteQuery,
  buildUserSessionClientsQuery,
  prefetchUserSessionClients,
  prefetchUserSessionClientsInfinite,
  queryKeyUserSessionClients,
  queryKeyUserSessionClientsInfinite,
  type UserSessionClientsInfiniteQueryData,
  type UserSessionClientsPageParams,
  type UserSessionClientsQueryData,
};

export type UserSessionClientsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listUserSessionClients userSessionClients
 *
 * @remarks
 * List user_session_clients in the caller's project.
 */
export function useUserSessionClients(
  request?: ListUserSessionClientsRequest | undefined,
  security?: ListUserSessionClientsSecurity | undefined,
  options?: QueryHookOptions<
    UserSessionClientsQueryData,
    UserSessionClientsQueryError
  >,
): UseQueryResult<UserSessionClientsQueryData, UserSessionClientsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildUserSessionClientsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listUserSessionClients userSessionClients
 *
 * @remarks
 * List user_session_clients in the caller's project.
 */
export function useUserSessionClientsSuspense(
  request?: ListUserSessionClientsRequest | undefined,
  security?: ListUserSessionClientsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    UserSessionClientsQueryData,
    UserSessionClientsQueryError
  >,
): UseSuspenseQueryResult<
  UserSessionClientsQueryData,
  UserSessionClientsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildUserSessionClientsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listUserSessionClients userSessionClients
 *
 * @remarks
 * List user_session_clients in the caller's project.
 */
export function useUserSessionClientsInfinite(
  request?: ListUserSessionClientsRequest | undefined,
  security?: ListUserSessionClientsSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    UserSessionClientsInfiniteQueryData,
    UserSessionClientsQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<
    UserSessionClientsInfiniteQueryData,
    UserSessionClientsPageParams
  >,
  UserSessionClientsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    UserSessionClientsInfiniteQueryData,
    UserSessionClientsQueryError,
    InfiniteData<
      UserSessionClientsInfiniteQueryData,
      UserSessionClientsPageParams
    >,
    QueryKey,
    UserSessionClientsPageParams
  >({
    ...buildUserSessionClientsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listUserSessionClients userSessionClients
 *
 * @remarks
 * List user_session_clients in the caller's project.
 */
export function useUserSessionClientsInfiniteSuspense(
  request?: ListUserSessionClientsRequest | undefined,
  security?: ListUserSessionClientsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    UserSessionClientsInfiniteQueryData,
    UserSessionClientsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<
    UserSessionClientsInfiniteQueryData,
    UserSessionClientsPageParams
  >,
  UserSessionClientsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    UserSessionClientsInfiniteQueryData,
    UserSessionClientsQueryError,
    InfiniteData<
      UserSessionClientsInfiniteQueryData,
      UserSessionClientsPageParams
    >,
    QueryKey,
    UserSessionClientsPageParams
  >({
    ...buildUserSessionClientsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setUserSessionClientsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      userSessionIssuerId?: string | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: UserSessionClientsQueryData,
): UserSessionClientsQueryData | undefined {
  const key = queryKeyUserSessionClients(...queryKeyBase);

  return client.setQueryData<UserSessionClientsQueryData>(key, data);
}

export function invalidateUserSessionClients(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      userSessionIssuerId?: string | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "userSessionClients", "list", ...queryKeyBase],
  });
}

export function invalidateAllUserSessionClients(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "userSessionClients", "list"],
  });
}
